import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { filter, difference, includes, isEmpty, map, uniq, find, some } from 'lodash-es';

import Modal from '../Modal';
import ListElement from './ListElement';
import NewListForm from './NewListForm';
import StoreSelection from './StoreSelection';
import LoadingOverlay from '../../components/LoadingOverlay';
import listConfig, { MARKET_LIST_TYPE, KEYWORD_LIST_TYPE } from './listTypeConfig';

const handlePreventScroll = action => document.body.classList[action]('noscroll');

const isStoreSelectionShown = (entities, listType) => {
  if (listType !== MARKET_LIST_TYPE) return false;

  return some(entities, entity => some(entity.app_stores, ({ store, ref_no }) => store === 'unified' && ref_no !== ''));
};

const selectedEntitiesByStore = (entities, selectedStore) =>
  entities.reduce((result, entity) => {
    // eslint-disable-next-line max-len
    const selectedEntityByStore = find(entity.app_stores, storeData => storeData.store === selectedStore) || {};

    if (!selectedEntityByStore.ref_no) return result;

    return [
      ...result,
      { ...selectedEntityByStore, app_stores: entity.app_stores },
    ];
  }, []);

class ListsModal extends Component {
  static propTypes = {
    onClose: PropTypes.func.isRequired,
    entities: PropTypes.array,
    currentStore: PropTypes.string,
    defaultSelectedStore: PropTypes.string,
    opened: PropTypes.bool.isRequired,
    listType: PropTypes.oneOf([MARKET_LIST_TYPE, KEYWORD_LIST_TYPE]).isRequired,
    updatedListName: PropTypes.string,
  }
  static defaultProps = {
    entities: [],
    updatedListName: '',
    currentStore: null,
    defaultSelectedStore: null,
  }

  state = {
    lists: [],
    entities: isStoreSelectionShown(this.props.entities, this.props.listType) ?
      selectedEntitiesByStore(this.props.entities, this.props.defaultSelectedStore) :
      this.props.entities,
    selectedStore: this.props.defaultSelectedStore,
    loading: true,
    errorMessage: '',
  }

  componentDidMount() {
    this.requestLists();
  }

  componentDidUpdate(prevProps) {
    if ((this.props.opened && !prevProps.opened) || (this.props.updatedListName.length > 0 && this.props.updatedListName !== prevProps.updatedListName)) {
      this.requestLists();
      handlePreventScroll('add');
    }
    if (!this.props.opened && prevProps.opened) {
      handlePreventScroll('remove');
    }
  }

  getListEntitiesIdsAfterReplace(list) {
    const entitiesToAdd = map(list.conflicts, m => m.entity_to_add);
    const entitiesToRemove = map(list.conflicts, m => m.entity_to_remove);
    const entitiesToAddIds = listConfig[this.props.listType].mappedEntitiesKeys(entitiesToAdd);
    const entitiesToRemoveIds = listConfig[this.props.listType].mappedEntitiesKeys(entitiesToRemove);
    const listEntitiesIds = uniq(
      map(
        filter(
          list.entities,
          entityId => !includes(entitiesToRemoveIds, entityId),
        ).concat(entitiesToAddIds),
        String,
      ),
    );
    return listEntitiesIds;
  }

  buildReplaceEntitiesParams(list) {
    const entitiesToAdd = map(list.conflicts, c => c.entity_to_add);
    const entitiesToRemove = map(list.conflicts, c => c.entity_to_remove);
    const paramsToAdd = listConfig[this.props.listType].prepareParameters(entitiesToAdd);
    const paramsToRemove = listConfig[this.props.listType].prepareParameters(entitiesToRemove);
    return {
      list_entities_to_add: paramsToAdd,
      list_entities_to_remove: paramsToRemove,
    };
  }

  mappedLists = () => {
    const { lists, entities } = this.state;

    return listConfig[this.props.listType].mappedLists(lists, entities);
  }
  handleAddToList = (id) => {
    const list = find(this.state.lists, { id });
    const { entities } = this.state;
    this.addEntities(list, entities);
  }
  handleRemoveFromList = (id) => {
    const list = find(this.state.lists, { id });
    const { entities } = this.state;
    this.removeEntities(list, entities);
  }
  handleReplaceInList = (id) => {
    const list = find(this.state.lists, { id });
    this.replaceEntities(list);
  }
  handleNewListAdded = (list) => {
    const { lists } = this.state;
    this.setState({ loading: false, lists: [list, ...lists] });
  }
  handleClose = () => this.setState({ errorMessage: '' }, this.props.onClose);

  handleStoreChange = (value) => {
    const { entities } = this.props;
    const { lists } = this.state;

    if (some(lists, ({ conflicts }) => conflicts && conflicts.length)) {
      this.setState({ loading: true }, () => {
        this.requestLists();
      });
    }
    this.setState({ selectedStore: value, entities: selectedEntitiesByStore(entities, value) });
  };

  requestLists = () => {
    const entityKind = this.props.entities[0].kind;
    listConfig[this.props.listType].requestList({ params: { entity_kind: entityKind } })
      .then(({ data }) => {
        this.setState({
          loading: false,
          lists: data,
        });
      });
  }
  addEntities = (list, entities) => {
    const params = listConfig[this.props.listType].prepareParameters(entities);
    this.setState({ loading: true }, () => {
      listConfig[this.props.listType].addEntities(list.id, params)
        .then(() => {
          this.setState({
            loading: false,
            lists: listConfig[this.props.listType].updateList(this.state.lists, list, {
              entities: uniq([...list.entities, ...listConfig[this.props.listType].mappedEntitiesKeys(entities)]),
            }),
            errorMessage: '',
          });
        })
        .catch(({ response: { data } }) => {
          const { conflicts } = data;
          if (!isEmpty(conflicts)) {
            this.setState({
              lists: listConfig[this.props.listType].updateList(this.state.lists, list, { conflicts }),
              loading: false,
            });
            return;
          }

          this.setState({ errorMessage: data.message ? data.message : 'An Error Occurred!', loading: false });
          this.handleDelayedCloseMessage();
        });
    });
  }
  replaceEntities = (list) => {
    const params = this.buildReplaceEntitiesParams(list);
    this.setState({ loading: true }, () => {
      listConfig[this.props.listType].replaceEntities(list.id, params)
        .then(() => {
          const lists = listConfig[this.props.listType].updateList(this.state.lists, list, {
            entities: this.getListEntitiesIdsAfterReplace(list),
            conflicts: [],
          });
          this.setState({
            loading: false,
            lists,
            errorMessage: '',
          });
        })
        .catch(({ response: { data } }) => {
          this.setState({ loading: false, errorMessage: data.message ? data.message : 'An Error Occurred!' });
          this.handleDelayedCloseMessage();
        });
    });
  }
  removeEntities = (list, entities) => {
    const params = listConfig[this.props.listType].prepareParameters(entities);
    this.setState({ loading: true }, () => {
      listConfig[this.props.listType].removeEntities(list.id, params)
        .then(() => {
          this.setState({
            loading: false,
            lists: listConfig[this.props.listType].updateList(this.state.lists, list, {
              entities: difference(list.entities, listConfig[this.props.listType].mappedEntitiesKeys(entities)),
            }),
            errorMessage: '',
          });
        })
        .catch(({ response: { data } }) => {
          this.setState({ loading: false, errorMessage: data.message ? data.message : 'An Error Occurred!' });
          this.handleDelayedCloseMessage();
        });
    });
  }
  handleLoadingOn = () => this.setState({ loading: true });
  handleDelayedCloseMessage = () => {
    setTimeout(() => this.setState({ errorMessage: '' }), 3000);
  }
  renderErrorMessage = errorMessage => (
    <div className="alert error market-error-message">
      <span className="mr-sm text-truncate">{errorMessage.replace(/<br>/g, '')}</span>
    </div>
  );

  renderListUpdateMessage = () => (
    <div className="alert success market-error-message">
      <span className="mr-sm text-truncate">{listConfig[this.props.listType].listName} <strong>{this.props.updatedListName}</strong> has been updated</span>
    </div>
  );

  render() {
    const {
      opened, listType, updatedListName, currentStore,
    } = this.props;
    const {
      lists, loading, errorMessage, entities, selectedStore,
    } = this.state;

    return (
      <Modal
        open={opened}
        modalClassName="add-to-market-modal"
        closeButtonVersionButton
        shouldCloseOnOverlayClick={false}
        onClose={this.handleClose}
      >
        <LoadingOverlay id="add-to-market-loading" loading={loading} />
        <div className="add-to-market-modal-header">
          <h2 className="add-to-market-modal-title">{listConfig[listType].modalTitle}</h2>
          {isStoreSelectionShown(this.props.entities, listType) &&
            <StoreSelection
              active={selectedStore}
              currentStore={currentStore}
              onChange={this.handleStoreChange}
            />
          }
          <NewListForm
            entities={entities}
            onSubmit={this.handleLoadingOn}
            onSubmitCallback={this.handleNewListAdded}
            loading={loading}
            listType={listType}
          />
          {errorMessage.length > 0 && this.renderErrorMessage(errorMessage) }
          {updatedListName.length > 0 && this.renderListUpdateMessage()}
        </div>
        <div className="add-to-market-modal-body">
          {!lists.length && !loading &&
            <div className="p-md text-quiet text-center">{listConfig[listType].noListsMessage}</div>
          }

          {lists.length > 0 &&
            <ul>
              {map(this.mappedLists(), list => (
                <ListElement
                  {...list}
                  key={list.id}
                  onAddClick={this.handleAddToList}
                  onRemoveClick={this.handleRemoveFromList}
                  onReplaceClick={this.handleReplaceInList}
                  listType={listType}
                />
              ))}
            </ul>
          }
        </div>
      </Modal>
    );
  }
}

export default ListsModal;
